import React, { useState, useEffect, useCallback } from "react";
import { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { ChatHeader } from "./ChatHeader";
import { ChatMessages } from "./ChatMessages";
import { ChatInput } from "./ChatInput";
import { TurnstileValidator } from "./TurnstileValidator";
import { useChat } from "@/hooks/useChat";
import { useTheme } from "@/hooks/useTheme";

export const ChatContainer = ({
  gpcService,
  licenseToken,
  widgetConfig,
  turnstileSiteKey,
  onClose,
  themeMode,
  showBranding = true,
  showMetadata = false,
  onError,
}) => {
  const { t } = useTranslation();
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [skipTurnstile, setSkipTurnstile] = useState(false);
  const { theme, toggleTheme } = useTheme(themeMode);

  const handleSessionExpired = useCallback(() => {
    setSkipTurnstile(false);
    setTurnstileToken(null);
    if (gpcService) {
      gpcService.renewSessionToken();
    }
  }, [gpcService]);

  const { messages, isLoading, sendMessage } = useChat(
    gpcService,
    turnstileToken,
    handleSessionExpired
  );

  useEffect(() => {
    if (gpcService && gpcService.hasValidCachedSession()) {
      setSkipTurnstile(true);
      setTurnstileToken('CACHED_SESSION');
    }
  }, [gpcService]);

  const handleTurnstileSuccess = (cfToken) => {
    setTurnstileToken(cfToken);
    setSkipTurnstile(true);
  };

  useEffect(() => {
    return () => {
      if (gpcService) {
        gpcService.releaseSession().catch((err) => {
          console.error("Failed to release session:", err);
        });
      }
    };
  }, [gpcService]);

  return (
    <div
      className={`gpc-chat-window gpc-animate-fade-in ${
        theme === "dark" ? "gpc-dark" : ""
      }`}
    >
      <ChatHeader
        onClose={onClose}
        theme={theme}
        onToggleTheme={toggleTheme}
        showBranding={showBranding}
        widgetConfig={widgetConfig}
      />

      {!skipTurnstile ? (
        <TurnstileValidator
          siteKey={turnstileSiteKey}
          onSuccess={handleTurnstileSuccess}
          onError={onError}
        />
      ) : (
        <>
          <ChatMessages
            messages={messages}
            isLoading={isLoading}
            showMetadata={showMetadata}
            widgetConfig={widgetConfig}
          />
          <ChatInput
            onSendMessage={sendMessage}
            isLoading={isLoading}
            disabled={false}
          />
        </>
      )}

      {showBranding && (
        <div className="gpc-footer">
          <span className="gpc-footer-text">{t("widget.poweredBy")}</span>
        </div>
      )}

      <Toaster position="top-center" />
    </div>
  );
};